import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  X,
  DollarSign,
  Calendar,
  CreditCard,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2,
  Info,
} from 'lucide-react';
import { adminInvoicesAPI } from '../../services/api';
import { formatMoney } from '../../utils/format';

// Fallback payment types if API call fails
const FALLBACK_PAYMENT_TYPES = [
  { id: 1, name: 'Credit Card', isDefault: true },
  { id: 2, name: 'ACH', isDefault: false },
  { id: 3, name: 'Check', isDefault: false },
  { id: 4, name: 'Cash', isDefault: false },
  { id: 5, name: 'Wire Transfer', isDefault: false },
  { id: 6, name: 'Other', isDefault: false },
];

function formatCurrency(amount) {
  return formatMoney(amount || 0);
}

export default function ApplyPaymentModal({ invoice, onClose, onSuccess }) {
  const [amount, setAmount] = useState(invoice?.balance?.toString() || '');
  // Use the browser's local-TZ "today" (YYYY-MM-DD) so 6 PM PDT doesn't
  // pre-fill tomorrow's date.
  const [paymentDate, setPaymentDate] = useState(
    new Date().toLocaleDateString('en-CA')
  );
  const [paymentType, setPaymentType] = useState('Credit Card');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);

  // Fetch payment types from API with fallback
  const { data: paymentTypesData } = useQuery({
    queryKey: ['payment-types'],
    queryFn: () => adminInvoicesAPI.getPaymentTypes(),
    staleTime: 60 * 60 * 1000, // Cache for 1 hour
  });
  const paymentTypes = paymentTypesData?.data?.paymentTypes || FALLBACK_PAYMENT_TYPES;

  const applyPaymentMutation = useMutation({
    mutationFn: (data) => adminInvoicesAPI.applyPayment(invoice.id, data),
    onSuccess: (response) => {
      onSuccess?.(response.data);
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to apply payment');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (parsedAmount > invoice.balance) {
      setError(`Amount cannot exceed balance (${formatCurrency(invoice.balance)})`);
      return;
    }

    applyPaymentMutation.mutate({
      amount: parsedAmount,
      paymentDate,
      paymentType,
      reference: reference || undefined,
      notes: notes || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Apply Payment
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Invoice Info */}
          <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-neutral-400" />
                <span className="text-sm font-medium text-neutral-900 dark:text-white">
                  Invoice #{invoice.invoiceNumber}
                </span>
              </div>
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                Balance: {formatCurrency(invoice.balance)}
              </span>
            </div>
            {/* Show credits if any are applied */}
            {invoice.credits > 0 && (
              <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                <Info className="w-3 h-3" />
                <span>Credits Applied: {formatCurrency(invoice.credits)}</span>
              </div>
            )}
            {/* Show downpayment info if any */}
            {invoice.downpaymentApplied > 0 && (
              <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                <Info className="w-3 h-3" />
                <span>Downpayment Applied: {formatCurrency(invoice.downpaymentApplied)}</span>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-2 text-red-700 dark:text-red-300 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Payment Amount *
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={invoice.balance}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0.00"
                required
              />
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Max: {formatCurrency(invoice.balance)}
            </p>
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Payment Date *
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </div>

          {/* Payment Type */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Payment Type *
            </label>
            <div className="relative">
              <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <select
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                required
              >
                {paymentTypes.map((type) => (
                  <option key={type.id || type.name} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Reference */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Reference / Transaction ID
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Check #12345, Transaction ID"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              placeholder="Optional notes about this payment"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600"
              disabled={applyPaymentMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={applyPaymentMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
            >
              {applyPaymentMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Apply Payment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
